import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiUpload, FiSave, FiImage } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Configuracion.css';

/* ══════════════════════════════════════════════════════════════════════════
   GENERIC CONFIG TABLE
   Columns: [{ key, label }]
   Fields:  [{ key, label, type:'text'|'select', options:[{value,label}] }]
   apiPath: e.g. '/api/config/unidades'
══════════════════════════════════════════════════════════════════════════ */
function ConfigTable({ title, columns, fields, apiPath, showSearch = true, businessId, canDelete = false }) {
  const [rows, setRows]       = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [modal, setModal]     = useState(null); // null | { mode:'new'|'edit', data:{} }
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modalError, setModalError] = useState('');
  const [delError, setDelError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`${apiPath}?business_id=${businessId}`);
      const data = res.data.data || [];
      setRows(data);
      setFiltered(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(rows); return; }
    const q = search.toLowerCase();
    setFiltered(rows.filter(r => columns.some(c => String(r[c.key] || '').toLowerCase().includes(q))));
    setPage(1);
  }, [search, rows, columns]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows   = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew  = () => {
    const blank = {};
    fields.forEach(f => { blank[f.key] = ''; });
    setForm(blank);
    setModalError('');
    setModal({ mode: 'new' });
  };
  const openEdit = (row) => {
    const data = {};
    fields.forEach(f => { data[f.key] = row[f.key] ?? ''; });
    setForm(data);
    setModalError('');
    setModal({ mode: 'edit', id: row.id });
  };
  const closeModal = () => { setModal(null); setModalError(''); };

  const handleSave = async () => {
    setSaving(true);
    setModalError('');
    try {
      if (modal.mode === 'new') {
        await api.post(`${apiPath}?business_id=${businessId}`, form);
      } else {
        await api.put(`${apiPath}/${modal.id}?business_id=${businessId}`, form);
      }
      await load();
      closeModal();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Error al guardar';
      const isDup = msg.includes('Duplicate') || msg.includes('ER_DUP');
      setModalError(isDup ? 'Ya existe un registro con ese nombre.' : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const label = row.name || row.short_name || `#${row.id}`;
    if (!window.confirm(`¿Eliminar "${label}"?`)) return;
    setDelError('');
    try {
      await api.delete(`${apiPath}/${row.id}?business_id=${businessId}`);
      await load();
    } catch (e) {
      setDelError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="cfg-page">
      {delError && <div className="cfg-error" style={{ margin: '0 0 8px' }}>{delError}</div>}
      {/* ── TOOLBAR ── */}
      <div className="cfg-toolbar">
        <button className="cfg-btn-nuevo" onClick={openNew}>
          <FiEdit2 /> Nuevo
        </button>
        {showSearch && (
          <div className="cfg-search-wrap">
            <span className="cfg-search-label">Busqueda Global</span>
            <input
              className="cfg-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
            />
          </div>
        )}
      </div>

      {/* ── PAGINATION TOP ── */}
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />

      {/* ── TABLE ── */}
      {loading ? (
        <div className="cfg-loading">Cargando...</div>
      ) : error ? (
        <div className="cfg-error">{error}</div>
      ) : (
        <table className="cfg-table">
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} className="cfg-th">{c.label}</th>
              ))}
              <th className="cfg-th cfg-th-action"></th>
            </tr>
            <tr className="cfg-filter-row">
              {columns.map(c => (
                <td key={c.key}>
                  <input className="cfg-col-filter" placeholder="" readOnly />
                </td>
              ))}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="cfg-empty">Sin datos</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={row.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                {columns.map(c => (
                  <td key={c.key} className="cfg-td">{row[c.key] ?? ''}</td>
                ))}
                <td className="cfg-td cfg-td-action">
                  <div className="ia-actions">
                    <button className="ia-btn ia-btn--edit" onClick={() => openEdit(row)} title="Editar">
                      <FiEdit2 size={14} />
                    </button>
                    {canDelete && (
                      <button className="ia-btn ia-btn--danger" onClick={() => handleDelete(row)} title="Eliminar">
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── PAGINATION BOTTOM ── */}
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />

      <div className="cfg-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* ── MODAL ── */}
      {modal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal" onClick={e => e.stopPropagation()}>
            <div className="cfg-modal-header">
              <span>Mantenimiento de {title}</span>
              <button className="cfg-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="cfg-modal-body">
              {fields.map(f => (
                <div className="cfg-field-row" key={f.key}>
                  <label className="cfg-field-label">{f.label}</label>
                  {f.type === 'select' ? (
                    <select
                      className="cfg-input"
                      value={form[f.key] || ''}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    >
                      <option value="">-- Seleccionar --</option>
                      {(f.options || []).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="cfg-input"
                      value={form[f.key] || ''}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    />
                  )}
                </div>
              ))}
            </div>
            {modalError && (
              <div className="cfg-modal-error">{modalError}</div>
            )}
            <div className="cfg-modal-footer">
              <button className="cfg-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal?.mode === 'new' ? 'Guardar' : 'Modificar'}
              </button>
              <button className="cfg-btn-cancel" onClick={closeModal}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Pagination component ─────────────────────────────────────────────── */
function Pagination({ page, total, count, perPage, onPage, onPerPage }) {
  return (
    <div className="cfg-pagination">
      <span className="cfg-page-info">({page} of {total})</span>
      <button onClick={() => onPage(1)}       disabled={page === 1}>|&lt;</button>
      <button onClick={() => onPage(page - 1)} disabled={page === 1}>&lt;&lt;</button>
      <span className="cfg-page-num">{page}</span>
      <button onClick={() => onPage(page + 1)} disabled={page === total}>&gt;&gt;</button>
      <button onClick={() => onPage(total)}   disabled={page === total}>&gt;|</button>
      <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}>
        {[5,10,20,50].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SUB-MÓDULOS
══════════════════════════════════════════════════════════════════════════ */

/* ── Serie Venta especial: el selector de Documento Pago es dinámico ─── */
function SerieVentaModule({ businessId }) {
  const [payDocs, setPayDocs] = useState([]);
  useEffect(() => {
    api.get(`/api/config/docs-pago?business_id=${businessId}`)
      .then(r => setPayDocs(r.data.data || []))
      .catch(() => {});
  }, [businessId]);

  const columns = [
    { key: 'doc_name', label: 'Doc. Compra Venta' },
    { key: 'serie',    label: 'Serie' },
    { key: 'sequence', label: 'Secuencia' },
  ];
  const fields = [
    {
      key: 'payment_document_id', label: 'Ingrese Documento Pago',
      type: 'select',
      options: payDocs.map(d => ({ value: d.id, label: d.name })),
    },
    { key: 'serie',    label: 'Serie' },
    { key: 'sequence', label: 'Secuencia' },
  ];

  return (
    <ConfigTable
      title="Series Venta"
      columns={columns}
      fields={fields}
      apiPath="/api/config/series-venta"
      showSearch={true}
      businessId={businessId}
    />
  );
}

/* ── Tipos de Movimiento — módulo con applies_to especial ───────────── */
export function TiposMovimientoModule({ businessId }) {
  const APPLIES_LABELS = { ingreso: 'INGRESO', salida: 'SALIDA', ambos: 'AMBOS' };
  const APPLIES_COLORS = { ingreso: '#16a34a', salida: '#dc2626', ambos: '#d97706' };

  const columns = [
    { key: 'name',       label: 'Tipo de Movimiento' },
    {
      key: 'applies_to', label: 'Aplica a',
      render: v => (
        <span style={{
          background: APPLIES_COLORS[v] + '18',
          color: APPLIES_COLORS[v],
          border: `1px solid ${APPLIES_COLORS[v]}44`,
          borderRadius: 12, padding: '2px 10px',
          fontWeight: 700, fontSize: 11
        }}>
          {APPLIES_LABELS[v] || v}
        </span>
      )
    },
  ];
  const fields = [
    { key: 'name', label: 'Nombre del Tipo' },
    {
      key: 'applies_to', label: 'Aplica a',
      type: 'select',
      options: [
        { value: 'ingreso', label: 'INGRESO' },
        { value: 'salida',  label: 'SALIDA' },
        { value: 'ambos',   label: 'AMBOS (Ingreso y Salida)' },
      ]
    },
  ];

  return (
    <ConfigTableWithRender
      title="Tipo Movimiento"
      columns={columns}
      fields={fields}
      apiPath="/api/config/tipos-movimiento"
      businessId={businessId}
    />
  );
}

/* ── ConfigTableWithRender — igual que ConfigTable pero soporta render() ─ */
function ConfigTableWithRender({ title, columns, fields, apiPath, businessId }) {
  const [rows, setRows]       = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modalError, setModalError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get(`${apiPath}?business_id=${businessId}`);
      const data = res.data.data || [];
      setRows(data); setFiltered(data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [apiPath, businessId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(rows); return; }
    const q = search.toLowerCase();
    setFiltered(rows.filter(r => columns.some(c => String(r[c.key] || '').toLowerCase().includes(q))));
    setPage(1);
  }, [search, rows, columns]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows   = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew  = () => {
    const blank = {}; fields.forEach(f => { blank[f.key] = f.type === 'select' ? (f.options?.[0]?.value || '') : ''; });
    setForm(blank); setModalError(''); setModal({ mode: 'new' });
  };
  const openEdit = (row) => {
    const data = {}; fields.forEach(f => { data[f.key] = row[f.key] ?? ''; });
    setForm(data); setModalError(''); setModal({ mode: 'edit', id: row.id });
  };

  const handleSave = async () => {
    setSaving(true); setModalError('');
    try {
      if (modal.mode === 'new') await api.post(`${apiPath}?business_id=${businessId}`, form);
      else await api.put(`${apiPath}/${modal.id}?business_id=${businessId}`, form);
      await load(); setModal(null); setModalError('');
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Error al guardar';
      const isDup = msg.includes('Duplicate') || msg.includes('ER_DUP');
      setModalError(isDup ? 'Ya existe un registro con ese nombre.' : msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="cfg-page">
      <div className="cfg-toolbar">
        <button className="cfg-btn-nuevo" onClick={openNew}><FiEdit2 /> Nuevo</button>
        <div className="cfg-search-wrap">
          <span className="cfg-search-label">Busqueda Global</span>
          <input className="cfg-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." />
        </div>
      </div>
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />
      {loading ? <div className="cfg-loading">Cargando...</div> : error ? <div className="cfg-error">{error}</div> : (
        <table className="cfg-table">
          <thead>
            <tr>{columns.map(c => <th key={c.key} className="cfg-th">{c.label}</th>)}<th className="cfg-th cfg-th-action"></th></tr>
            <tr className="cfg-filter-row">
              {columns.map(c => <td key={c.key}><input className="cfg-col-filter" readOnly /></td>)}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0
              ? <tr><td colSpan={columns.length + 1} className="cfg-empty">Sin datos</td></tr>
              : pageRows.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                  {columns.map(c => (
                    <td key={c.key} className="cfg-td">
                      {c.render ? c.render(row[c.key]) : (row[c.key] ?? '')}
                    </td>
                  ))}
                  <td className="cfg-td cfg-td-action">
                    <button className="cfg-btn-edit" onClick={() => openEdit(row)}><FiEdit2 /></button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      )}
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />
      <div className="cfg-footer">Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com</div>
      {modal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal" onClick={e => e.stopPropagation()}>
            <div className="cfg-modal-header">
              <span>Mantenimiento de {title}</span>
              <button className="cfg-modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="cfg-modal-body">
              {fields.map(f => (
                <div className="cfg-field-row" key={f.key}>
                  <label className="cfg-field-label">{f.label}</label>
                  {f.type === 'select' ? (
                    <select className="cfg-input" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}>
                      {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input className="cfg-input" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
              {modalError && <div className="cfg-modal-error">{modalError}</div>}
            </div>
            <div className="cfg-modal-footer">
              <button className="cfg-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal?.mode === 'new' ? 'Guardar' : 'Modificar'}
              </button>
              <button className="cfg-btn-cancel" onClick={() => { setModal(null); setModalError(''); }}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Módulos simples ────────────────────────────────────────────────── */
export function UnidadesModule({ businessId }) {
  return (
    <ConfigTable
      title="Unidad de Medida"
      columns={[
        { key: 'short_name', label: 'Descripcion Corta' },
        { key: 'name',       label: 'Descripcion' },
      ]}
      fields={[
        { key: 'short_name', label: 'Descripcion Corta' },
        { key: 'name',       label: 'Descripcion' },
      ]}
      apiPath="/api/config/unidades"
      showSearch={false}
      businessId={businessId}
      canDelete={true}
    />
  );
}

export function FormasPagoModule({ businessId }) {
  return (
    <ConfigTable
      title="Forma Pago"
      columns={[
        { key: 'name',       label: 'Forma de Pago' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      fields={[
        { key: 'name',       label: 'Forma de Pago' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      apiPath="/api/config/formas-pago"
      showSearch={false}
      businessId={businessId}
    />
  );
}

export function TiposDocumentoModule({ businessId }) {
  return (
    <ConfigTable
      title="Tipo Documento"
      columns={[
        { key: 'name',       label: 'Tipo Documento' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      fields={[
        { key: 'name',       label: 'Tipo Documento' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      apiPath="/api/config/tipos-documento"
      showSearch={false}
      businessId={businessId}
    />
  );
}

export function DocsPagoModule({ businessId }) {
  return (
    <ConfigTable
      title="Documentos Pago"
      columns={[
        { key: 'name',       label: 'Doc. Compra Venta' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      fields={[
        { key: 'name',       label: 'Doc. Compra Venta' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      apiPath="/api/config/docs-pago"
      businessId={businessId}
    />
  );
}

/* ── Marcas ─────────────────────────────────────────────────────────────── */
export function MarcasModule({ businessId }) {
  return (
    <ConfigTable
      title="Marcas"
      columns={[{ key: 'nombre', label: 'Marca' }]}
      fields={[{ key: 'nombre', label: 'Nombre de Marca' }]}
      apiPath="/api/config/marcas"
      showSearch={false}
      businessId={businessId}
    />
  );
}

/* ── Perfiles ───────────────────────────────────────────────────────────── */
export function PerfilesModule({ businessId }) {
  const ADMIN_LABELS = { true: 'Sí', false: 'No', 1: 'Sí', 0: 'No' };

  const columns = [
    { key: 'nombre',      label: 'Perfil' },
    { key: 'descripcion', label: 'Descripción' },
    {
      key: 'es_admin', label: 'Admin',
      render: v => (
        <span style={{
          background: (v ? '#16a34a' : '#6b7280') + '18',
          color: v ? '#16a34a' : '#6b7280',
          border: `1px solid ${v ? '#16a34a' : '#6b7280'}44`,
          borderRadius: 12, padding: '2px 10px',
          fontWeight: 700, fontSize: 11
        }}>
          {ADMIN_LABELS[v] || 'No'}
        </span>
      )
    },
  ];
  const fields = [
    { key: 'nombre',      label: 'Nombre del Perfil' },
    { key: 'descripcion', label: 'Descripción' },
    {
      key: 'es_admin', label: '¿Es Administrador?',
      type: 'select',
      options: [
        { value: '0', label: 'No' },
        { value: '1', label: 'Sí — Acceso total' },
      ]
    },
  ];

  return (
    <ConfigTableWithRender
      title="Perfiles"
      columns={columns}
      fields={fields}
      apiPath="/api/config/perfiles"
      businessId={businessId}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   VALORES — Stock negativo por almacén
══════════════════════════════════════════════════════════════════════════ */
/* ── Sección datos de empresa ───────────────────────────────────────────────── */
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function EmpresaSection() {
  const { empresa: empCtx, refreshEmpresa } = useAuth();

  const [form, setForm]     = useState({ razon_social: '', ruc: '', telefono: '', correo: '', direccion: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [uploadingImg, setUploadingImg] = useState(null); // 'logo'|'fondo_inicio'|'fondo_general'|null

  const logoRef    = useRef();
  const fondoIRef  = useRef();
  const fondoGRef  = useRef();

  // Carga datos actuales de empresa
  useEffect(() => {
    api.get('/api/empresa').then(r => {
      const e = r.data.empresa || {};
      setForm({
        razon_social: e.razon_social || '',
        ruc:          e.ruc          || '',
        telefono:     e.telefono     || '',
        correo:       e.correo       || '',
        direccion:    e.direccion    || '',
      });
    }).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.razon_social.trim()) { setMsg('La razón social es requerida'); return; }
    setSaving(true); setMsg('');
    try {
      await api.put('/api/empresa', form);
      await refreshEmpresa();
      setMsg('✓ Datos guardados');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) { setMsg(err.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (campo, file) => {
    if (!file) return;
    setUploadingImg(campo); setMsg('');
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      await api.post(`/api/empresa/upload/${campo}`, fd, { headers: { 'Content-Type': undefined } });
      await refreshEmpresa();
      setMsg(`✓ Imagen guardada`);
      setTimeout(() => setMsg(''), 3000);
    } catch (err) { setMsg(err.response?.data?.error || 'Error al subir imagen'); }
    finally { setUploadingImg(null); }
  };

  const imgSrc = (campo) => {
    const ruta = empCtx?.[campo];
    return ruta ? `${API_URL}${ruta}` : null;
  };

  const ImageUploader = ({ campo, label, refEl }) => {
    const src = imgSrc(campo);
    return (
      <div className="val-img-uploader">
        <div className="val-img-label">{label}</div>
        <div
          className="val-img-preview"
          onClick={() => refEl.current?.click()}
          title="Clic para cambiar"
        >
          {src ? (
            <img src={src} alt={label} className="val-img-thumb"
              onError={e => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="val-img-empty"><FiImage size={28} /><span>Sin imagen</span></div>
          )}
          {uploadingImg === campo && <div className="val-img-overlay">Subiendo…</div>}
        </div>
        <button
          type="button"
          className="val-img-btn"
          onClick={() => refEl.current?.click()}
          disabled={!!uploadingImg}
        >
          <FiUpload size={13} /> Cambiar
        </button>
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={refEl}
          onChange={e => handleUpload(campo, e.target.files[0])}
        />
      </div>
    );
  };

  return (
    <div className="val-empresa-section">
      <div className="val-section-title">Datos de la Empresa</div>

      <form className="val-empresa-form" onSubmit={handleSave}>
        <div className="val-empresa-fields">
          <div className="val-field">
            <label>Nombre / Razón Social</label>
            <input className="cfg-input" value={form.razon_social}
              onChange={e => setForm(p => ({ ...p, razon_social: e.target.value }))} />
          </div>
          <div className="val-field">
            <label>RUC</label>
            <input className="cfg-input" value={form.ruc}
              onChange={e => setForm(p => ({ ...p, ruc: e.target.value }))} maxLength={11} />
          </div>
          <div className="val-field">
            <label>Teléfono</label>
            <input className="cfg-input" value={form.telefono}
              onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))} />
          </div>
          <div className="val-field">
            <label>Correo</label>
            <input className="cfg-input" type="email" value={form.correo}
              onChange={e => setForm(p => ({ ...p, correo: e.target.value }))} />
          </div>
          <div className="val-field val-field-full">
            <label>Dirección</label>
            <input className="cfg-input" value={form.direccion}
              onChange={e => setForm(p => ({ ...p, direccion: e.target.value }))} />
          </div>
        </div>

        <div className="val-empresa-imgs">
          <ImageUploader campo="logo"         label="Logo Empresa"       refEl={logoRef} />
          <ImageUploader campo="fondo_inicio"  label="Fondo Inicio"       refEl={fondoIRef} />
          <ImageUploader campo="fondo_general" label="Fondo General"      refEl={fondoGRef} />
        </div>

        <div className="val-empresa-footer">
          {msg && <span className={msg.startsWith('✓') ? 'val-msg-ok' : 'val-msg-err'}>{msg}</span>}
          <button type="submit" className="cfg-btn-nuevo" disabled={saving}>
            <FiSave size={13} /> {saving ? 'Guardando…' : 'Guardar datos'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── CajaModule: permisos de usuarios para el módulo de caja ────────────────── */
function CajaModule() {
  const [usuarios, setUsuarios] = useState([]);
  const [permisos, setPermisos] = useState({}); // { [usuario_id]: { puede_modificar_precio } }
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [ok,       setOk]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/caja-config/usuarios');
      const data = r.data.data || [];
      setUsuarios(data);
      const map = {};
      for (const u of data) {
        map[u.id] = { puede_modificar_precio: !!u.puede_modificar_precio };
      }
      setPermisos(map);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePrecio = (uid) => {
    setPermisos(prev => ({
      ...prev,
      [uid]: { ...prev[uid], puede_modificar_precio: !prev[uid]?.puede_modificar_precio },
    }));
    setOk(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setOk(false);
    try {
      const lista = usuarios.map(u => ({
        usuario_id:            u.id,
        puede_modificar_precio: permisos[u.id]?.puede_modificar_precio ? 1 : 0,
      }));
      await api.post('/api/caja-config/permisos', { permisos: lista });
      setOk(true);
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const badgeStyle = (active, color) => ({
    fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 12,
    color:      active ? color : '#6b7280',
    background: (active ? color : '#6b7280') + '14',
    border:     `1px solid ${(active ? color : '#6b7280')}44`,
  });

  return (
    <>
      <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 1 }}>
        Caja — Permisos de usuarios
      </div>
      <div style={{ padding: '0 16px 8px', fontSize: 11, color: '#64748b' }}>
        Los administradores siempre tienen acceso total. Aquí se configuran los usuarios con rol "usuario".
      </div>

      {loading ? <div className="cfg-loading">Cargando...</div>
       : error  ? <div className="cfg-error">{error}</div>
       : usuarios.length === 0 ? (
          <div style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>
            No hay usuarios (no administradores) registrados y activos.
          </div>
        ) : (
        <table className="cfg-table">
          <thead>
            <tr>
              <th className="cfg-th">Usuario</th>
              <th className="cfg-th">Nombre</th>
              <th className="cfg-th" style={{ textAlign: 'center', width: 200 }}>Puede Modificar Precio</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u, i) => (
              <tr key={u.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                <td className="cfg-td" style={{ fontWeight: 600 }}>{u.nombre_usuario}</td>
                <td className="cfg-td">{u.nombre_completo}</td>
                <td className="cfg-td" style={{ textAlign: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={!!permisos[u.id]?.puede_modificar_precio}
                      onChange={() => togglePrecio(u.id)}
                      style={{ accentColor: '#16a34a', width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <span style={badgeStyle(!!permisos[u.id]?.puede_modificar_precio, '#16a34a')}>
                      {permisos[u.id]?.puede_modificar_precio ? 'HABILITADO' : 'DESHABILITADO'}
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && (
        <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#1e40af', color: '#fff', border: 'none', borderRadius: 6,
              padding: '7px 20px', fontWeight: 700, fontSize: 13, cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Guardando...' : 'Guardar permisos'}
          </button>
          {ok && <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 12 }}>✓ Guardado correctamente</span>}
        </div>
      )}
    </>
  );
}

/* ── ValoresModule (almacenes + empresa + caja) ─────────────────────────────── */
export function ValoresModule() {
  const [almacenes, setAlmacenes] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [saving,    setSaving]    = useState(null); // id del almacén en proceso

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/almacenes');
      setAlmacenes(r.data.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (alm) => {
    setSaving(alm.id);
    try {
      await api.patch(`/api/almacenes/${alm.id}/stock-negativo`, {
        permite_stock_negativo: !alm.permite_stock_negativo,
      });
      setAlmacenes(prev => prev.map(a =>
        a.id === alm.id ? { ...a, permite_stock_negativo: !alm.permite_stock_negativo } : a
      ));
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(null); }
  };

  return (
    <div className="cfg-page">
      <div className="cfg-toolbar">
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
          Configuración de valores
        </span>
      </div>

      <EmpresaSection />

      <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 1 }}>
        Stock por almacén
      </div>

      {loading ? <div className="cfg-loading">Cargando...</div>
       : error  ? <div className="cfg-error">{error}</div>
       : (
        <table className="cfg-table">
          <thead>
            <tr>
              <th className="cfg-th">Almacén</th>
              <th className="cfg-th">Descripción</th>
              <th className="cfg-th" style={{ textAlign: 'center', width: 180 }}>Permite Stock Negativo</th>
            </tr>
          </thead>
          <tbody>
            {almacenes.length === 0
              ? <tr><td colSpan={3} className="cfg-empty">Sin almacenes registrados</td></tr>
              : almacenes.map((a, i) => (
                <tr key={a.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                  <td className="cfg-td" style={{ fontWeight: 600 }}>{a.nombre}</td>
                  <td className="cfg-td">{a.descripcion || '—'}</td>
                  <td className="cfg-td" style={{ textAlign: 'center' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: saving === a.id ? 'wait' : 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={!!a.permite_stock_negativo}
                        disabled={saving === a.id}
                        onChange={() => handleToggle(a)}
                        style={{ accentColor: '#dc2626', width: 16, height: 16, cursor: 'pointer' }}
                      />
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 12,
                        color:       a.permite_stock_negativo ? '#dc2626' : '#6b7280',
                        background: (a.permite_stock_negativo ? '#dc2626' : '#6b7280') + '14',
                        border:     `1px solid ${(a.permite_stock_negativo ? '#dc2626' : '#6b7280')}44`,
                      }}>
                        {a.permite_stock_negativo ? 'HABILITADO' : 'DESHABILITADO'}
                      </span>
                    </label>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      )}

      <CajaModule />

      <div className="cfg-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ROUTER
══════════════════════════════════════════════════════════════════════════ */
function ConfiguracionPage({ businessId }) {
  return (
    <Routes>
      <Route path="unidades"         element={<UnidadesModule        businessId={businessId} />} />
      <Route path="formas-pago"      element={<FormasPagoModule      businessId={businessId} />} />
      <Route path="tipos-documento"  element={<TiposDocumentoModule  businessId={businessId} />} />
      <Route path="docs-pago"        element={<DocsPagoModule        businessId={businessId} />} />
      <Route path="series-venta"      element={<SerieVentaModule         businessId={businessId} />} />
      <Route path="tipos-movimiento"  element={<TiposMovimientoModule    businessId={businessId} />} />
      <Route path="perfiles"          element={<PerfilesModule           businessId={businessId} />} />
      <Route path="valores"           element={<ValoresModule />} />
      <Route path="*"                 element={<UnidadesModule           businessId={businessId} />} />
    </Routes>
  );
}

export default ConfiguracionPage;
